import React, { useCallback, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'

import SkeletonListRow from '../../../../features/components/List/Skeleton'
import PageSection from '../../../../features/components/PageSection'
import List from '../../../../features/components/List'
import ItemsLoading from '../../../../components/Loaders/ItemsLoading'
import ActionsDropdown from '../../../../features/components/ActionsDropdown'
import Chip from '../../../../components/Chip'

import { deleteMarginOverride, clearDeleteMarginOverride } from '../../../../modules/actions/margins'
import {
  marginsErrorSelector,
  marginsIsLoadingSelector,
  marginsSelector,
  marginsWasLoadedSelector,
  deleteMarginOverrideIdSelector,
  deleteMarginOverrideIsLoadingSelector,
  deleteMarginOverrideErrorSelector,
  deleteMarginOverrideWasDeletedSelector
} from '../../../../modules/selectors/margins'

import { CHIP_COLORS } from '../../../../constants/other'

import useCommonStyles from '../../../../styles/common/listPage'

const MarginsList = () => {
  const commonClasses = useCommonStyles()

  const { t } = useTranslation()
  const dispatch = useDispatch()

  const margins = useSelector(marginsSelector)
  const deleteMarginOverrideId = useSelector(deleteMarginOverrideIdSelector)
  const deleteMarginOverrideIsLoading = useSelector(deleteMarginOverrideIsLoadingSelector)
  const deleteMarginOverrideWasDeleted = useSelector(deleteMarginOverrideWasDeletedSelector)
  const deleteMarginOverrideError = useSelector(deleteMarginOverrideErrorSelector)

  const deleteMarginOverrideHandler = useCallback(
    id => {
      dispatch(deleteMarginOverride(id))
    },
    [dispatch]
  )

  const getMarginOverrideActionsDropdownOptions = useCallback(
    id => {
      return [
        {
          text: t('Delete'),
          onClickHandler: () => deleteMarginOverrideHandler(id)
        }
      ]
    },
    [t, deleteMarginOverrideHandler]
  )

  const marginsListColumns = useMemo(
    () => [
      {
        headTitle: 'Name',
        Component: ({ labels }) => {
          return labels.join(', ')
        },
        style: {
          minWidth: 295,
          maxWidth: 295,
          whiteSpace: 'normal'
        }
      },
      {
        headTitle: '',
        Component: ({ date_label: date }) => {
          return date && <Chip text={date} />
        },
        style: {
          display: 'flex',
          justifyContent: 'flex-end',
          minWidth: 190,
          maxWidth: 190
        }
      },
      {
        headTitle: '',
        Component: ({ active_label: status }) => {
          const isMarginDisabled = status.toLowerCase() === 'previous'

          return <Chip text={status} color={isMarginDisabled ? CHIP_COLORS.neutral : CHIP_COLORS.success} />
        },
        style: {
          display: 'flex',
          justifyContent: 'flex-end',
          minWidth: 85,
          maxWidth: 85,
          paddingRight: 0,
          paddingLeft: 6
        }
      },
      {
        Component: ({ id }) => {
          // override margins have id field, while usual margins don't
          const isMarginOverride = !!id

          return isMarginOverride && <ActionsDropdown options={getMarginOverrideActionsDropdownOptions(id)} />
        },
        style: {
          minWidth: 30,
          maxWidth: 30
        }
      }
    ],
    [getMarginOverrideActionsDropdownOptions]
  )

  const skeletonProps = useMemo(() => ({ cols: marginsListColumns }), [marginsListColumns])

  useEffect(() => {
    if (deleteMarginOverrideWasDeleted || deleteMarginOverrideError) {
      dispatch(clearDeleteMarginOverride())
    }
  }, [dispatch, deleteMarginOverrideError, deleteMarginOverrideWasDeleted])

  return (
    <ItemsLoading
      className={commonClasses.itemList}
      itemsLength={margins.length}
      SkeletonComponent={SkeletonListRow}
      skeletonProps={skeletonProps}
      ContentWrapper={PageSection}
      errorSelector={marginsErrorSelector}
      isLoadingSelector={marginsIsLoadingSelector}
      wasLoadedSelector={marginsWasLoadedSelector}
      isEmptyNoDataMessage
    >
      <List
        cols={marginsListColumns}
        listData={margins}
        itemDeletingId={deleteMarginOverrideIsLoading ? deleteMarginOverrideId : ''}
      />
    </ItemsLoading>
  )
}

export default MarginsList
